/**
 * "Stationery mecha" enemy set: every silhouette is a piece of stationery
 * turned into a war machine, inked with a bold outline and cel shading.
 * Static bodies are cached as sprites; moving parts are drawn live.
 * All enemies face down (+y), towards the player.
 */
import type { Enemy } from "../types";
import {
  INK,
  TAU,
  box,
  clamp,
  disc,
  glow,
  hash,
  line,
  linear,
  oval,
  poly,
  radial,
  shade,
  silhouette,
  stamp,
} from "./kit";

type Ctx = CanvasRenderingContext2D;
/** Danger accent shared with enemy bullets: warm red/magenta reads as "enemy". */
export const DANGER = "#FF3D5E";

const lw = (r: number) => Math.max(2.2, r * 0.095);
function lit(c: Ctx, r: number, base: string, spread = 0.26) {
  return linear(c, -r, -r, r, r, [
    [0, shade(base, spread)],
    [0.55, base],
    [1, shade(base, -spread)],
  ]);
}
function eye(c: Ctx, x: number, y: number, r: number, color = DANGER) {
  disc(c, x, y, r * 1.35, INK);
  disc(c, x, y, r, radial(c, x, y, 0, r, [[0, "#FFFFFF"], [0.35, shade(color, 0.3)], [1, color]]));
  disc(c, x - r * 0.35, y - r * 0.35, r * 0.28, "#FFFFFF");
}
function rivets(c: Ctx, pts: number[][], r: number) {
  for (const [x, y] of pts) disc(c, x, y, r, "rgba(20,24,36,0.45)");
}
function canopy(c: Ctx, x: number, y: number, rx: number, ry: number) {
  oval(c, x, y, rx, ry, linear(c, x - rx, y - ry, x + rx, y + ry, [[0, "#CFF6FF"], [0.5, "#4FA6C8"], [1, "#1D4E73"]]), INK, 2);
  oval(c, x - rx * 0.3, y - ry * 0.35, rx * 0.35, ry * 0.25, "rgba(255,255,255,0.8)");
}

interface Design {
  /** Static sprite, drawn in local coordinates at radius r. */
  body(c: Ctx, r: number): void;
  under?(c: Ctx, e: Enemy, r: number, t: number): void;
  over?(c: Ctx, e: Enemy, r: number, t: number): void;
  spin?(e: Enemy, t: number): number;
  /** Sprite box in radii (default 3.4). */
  span?: number;
}

const streamer = (c: Ctx, x: number, y: number, len: number, width: number, t: number, colors: string[]) => {
  const steps = 10;
  for (let i = 0; i < steps; i++) {
    const a = i / steps,
      b = (i + 1) / steps;
    const p0 = [x + Math.sin(t * 0.18 + a * 5) * len * 0.16 * a, y - a * len],
      p1 = [x + Math.sin(t * 0.18 + b * 5) * len * 0.16 * b, y - b * len];
    line(c, [p0, p1], colors[i % colors.length], width * (1 - a * 0.6), "butt");
  }
};

const DESIGNS: Design[] = [
  // 0 直冲纸燕 — origami dart with crease facets.
  {
    under(c, e, r, t) {
      for (const s of [-1, 1]) streamer(c, s * r * 0.28, -r * 0.4, r * 1.3, r * 0.14, t + s * 9, [DANGER, "#FFB44A"]);
    },
    body(c, r) {
      const w = lw(r);
      for (const s of [-1, 1]) {
        poly(c, [[0, r * 1.05], [s * r * 1.08, -r * 0.72], [s * r * 0.36, -r * 0.56]], "#D4CBB8", INK, w);
        poly(c, [[0, r * 1.05], [s * r * 0.36, -r * 0.56], [0, -r * 0.4]], s < 0 ? "#FBF6EA" : "#EDE4D2", INK, w);
        poly(c, [[s * r * 1.08, -r * 0.72], [s * r * 0.82, -r * 0.3], [s * r * 0.68, -r * 0.62]], DANGER, INK, w * 0.7);
      }
      line(c, [[0, r * 1.0], [0, -r * 0.36]], "rgba(120,100,80,0.5)", 1.5);
      eye(c, 0, r * 0.15, r * 0.17);
    },
  },
  // 1 蛇形彩带 — drone head dragging a striped ribbon.
  {
    under(c, e, r, t) {
      const colors = ["#FF5C8A", "#FFC94D", "#56C7E0", "#9F7BFF"];
      const pts: number[][] = [];
      for (let i = 0; i <= 14; i++) {
        const a = i / 14;
        pts.push([Math.sin(t * 0.12 - a * 6) * r * 0.7 * a, -r * 0.4 - a * r * 3.4]);
      }
      for (let i = 0; i < 14; i++) line(c, [pts[i], pts[i + 1]], INK, r * 0.62 * (1 - (i / 14) * 0.7) + 3, "round");
      for (let i = 0; i < 14; i++) line(c, [pts[i], pts[i + 1]], colors[i % 4], r * 0.62 * (1 - (i / 14) * 0.7), "round");
    },
    body(c, r) {
      const w = lw(r);
      for (const s of [-1, 1]) poly(c, [[s * r * 0.4, -r * 0.1], [s * r * 1.0, -r * 0.45], [s * r * 0.8, r * 0.25]], "#8E3A6A", INK, w);
      disc(c, 0, 0, r * 0.66, lit(c, r * 0.66, "#D6457A"), INK, w);
      box(c, -r * 0.46, r * 0.02, r * 0.92, r * 0.34, r * 0.17, "#2A2140", INK, w * 0.7);
      eye(c, 0, r * 0.19, r * 0.13, "#FFD166");
      oval(c, -r * 0.25, -r * 0.35, r * 0.2, r * 0.1, "rgba(255,255,255,0.55)", null, 0, -0.5);
    },
  },
  // 2 横移扫射 — wide gunship with under-wing cannon pods.
  {
    over(c, e, r, t) {
      for (const s of [-1, 1]) glow(c, s * r * 0.3, -r * 0.95, r * (0.35 + 0.08 * Math.sin(t * 0.6 + s)), "#FF9A3D", 0.9);
    },
    body(c, r) {
      const w = lw(r);
      for (const s of [-1, 1]) {
        poly(c, [[s * r * 0.3, -r * 0.4], [s * r * 1.38, -r * 0.05], [s * r * 1.38, r * 0.3], [s * r * 0.3, r * 0.35]], lit(c, r, "#4E6089"), INK, w);
        box(c, s * r * 0.9 - r * 0.13, -r * 0.2, r * 0.26, r * 0.95, r * 0.12, "#2F3950", INK, w * 0.8);
        disc(c, s * r * 0.9, r * 0.75, r * 0.1, "#FF9A3D");
        poly(c, [[s * r * 1.38, -r * 0.05], [s * r * 1.15, -r * 0.02], [s * r * 1.15, r * 0.28], [s * r * 1.38, r * 0.3]], DANGER, null);
      }
      box(c, -r * 0.34, -r * 0.95, r * 0.68, r * 1.8, r * 0.3, lit(c, r, "#5C6F9C"), INK, w);
      for (const s of [-1, 1]) box(c, s * r * 0.3 - r * 0.1, -r * 1.05, r * 0.2, r * 0.25, r * 0.06, "#2F3950", INK, w * 0.7);
      canopy(c, 0, r * 0.35, r * 0.2, r * 0.32);
      line(c, [[-r * 0.2, -r * 0.4], [r * 0.2, -r * 0.4]], "rgba(20,24,36,0.35)", 1.5);
    },
  },
  // 3 瞄准铅笔 — sharpened pencil with fins and a laser tip.
  {
    over(c, e, r, t) {
      if (e.warnAt && e.warnAt > t) glow(c, 0, r * 1.25, r * (0.5 + 0.2 * Math.sin(t * 0.8)), DANGER, 1);
    },
    body(c, r) {
      const w = lw(r),
        hw = r * 0.36;
      for (const s of [-1, 1]) poly(c, [[s * hw, -r * 0.55], [s * r * 0.85, -r * 0.2], [s * r * 0.8, r * 0.1], [s * hw, r * 0.05]], DANGER, INK, w);
      poly(c, [[-hw, -r * 0.95], [hw, -r * 0.95], [hw, r * 0.35], [-hw, r * 0.35]], "#E8B82A", INK, w);
      c.fillStyle = "#FFD957";
      c.fillRect(-hw * 0.35, -r * 0.93, hw * 0.7, r * 1.26);
      c.fillStyle = "#C9961C";
      c.fillRect(hw * 0.45, -r * 0.93, hw * 0.5, r * 1.26);
      poly(c, [[-hw, r * 0.35], [hw, r * 0.35], [0, r * 1.25]], "#EBC894", INK, w);
      poly(c, [[-hw * 0.34, r * 0.94], [hw * 0.34, r * 0.94], [0, r * 1.25]], "#2D2F3A", INK, w * 0.6);
      box(c, -hw - 1, -r * 1.2, hw * 2 + 2, r * 0.3, 2, linear(c, -hw, 0, hw, 0, [[0, "#9AA3B0"], [0.4, "#F2F5F8"], [1, "#7C8593"]]), INK, w * 0.8);
      box(c, -hw, -r * 1.42, hw * 2, r * 0.26, r * 0.1, "#F29CB0", INK, w * 0.8);
    },
  },
  // 4 环形花火 — rotating battery of launch tubes around a hot core.
  {
    spin: (e) => e.age * 0.03,
    over(c, e, r, t) {
      glow(c, 0, 0, r * (0.7 + 0.2 * Math.sin(t * 0.25)), "#FF5CA8", 0.85);
      disc(c, 0, 0, r * 0.2, "#FFF4FA");
    },
    body(c, r) {
      const w = lw(r),
        colors = [DANGER, "#FFC94D", "#56C7E0", "#FFC94D"];
      for (let i = 0; i < 8; i++) {
        c.save();
        c.rotate((i / 8) * TAU);
        box(c, -r * 0.17, -r * 1.05, r * 0.34, r * 0.6, r * 0.08, colors[i % 4], INK, w * 0.8);
        oval(c, 0, -r * 1.02, r * 0.12, r * 0.06, INK);
        c.restore();
      }
      disc(c, 0, 0, r * 0.62, lit(c, r * 0.62, "#3A447A"), INK, w);
      c.beginPath();
      c.arc(0, 0, r * 0.5, 0, TAU);
      c.strokeStyle = "#E7B94B";
      c.lineWidth = w;
      c.stroke();
      disc(c, 0, 0, r * 0.34, "#5A1E48", INK, w * 0.7);
    },
  },
  // 5 护盾纸甲 — layered hex armour behind a shimmering barrier.
  {
    over(c, e, r, t) {
      c.save();
      c.globalAlpha = 0.5 + 0.2 * Math.sin(t * 0.12);
      c.strokeStyle = "#7FF3FF";
      c.lineWidth = 3;
      c.beginPath();
      for (let i = 0; i <= 6; i++) {
        const a = (i / 6) * TAU + t * 0.01;
        c.lineTo(Math.cos(a) * (r + 9), Math.sin(a) * (r + 9));
      }
      c.stroke();
      c.globalAlpha *= 0.4;
      c.fillStyle = "#7FF3FF";
      c.fill();
      c.restore();
    },
    body(c, r) {
      const w = lw(r),
        hex = (k: number) => Array.from({ length: 6 }, (_, i) => [Math.cos((i / 6) * TAU + Math.PI / 6) * r * k, Math.sin((i / 6) * TAU + Math.PI / 6) * r * k]);
      poly(c, hex(1), lit(c, r, "#4F7F86"), INK, w);
      poly(c, hex(0.72), lit(c, r, "#7DB0AE", 0.2), INK, w * 0.8);
      poly(c, hex(0.42), "#35545C", INK, w * 0.7);
      rivets(c, hex(0.86), r * 0.06);
      eye(c, 0, r * 0.05, r * 0.16);
    },
  },
  // 6 分裂纸团 — crumpled paper ball glowing through its creases.
  {
    spin: (e) => e.age * 0.02,
    over(c, e, r, t) {
      glow(c, 0, 0, r * 0.9, "#FF8A3D", 0.35 + 0.15 * Math.sin(t * 0.3));
    },
    body(c, r) {
      const w = lw(r),
        pts = Array.from({ length: 11 }, (_, i) => {
          const a = (i / 11) * TAU,
            k = 0.82 + hash(6, i) * 0.22;
          return [Math.cos(a) * r * k, Math.sin(a) * r * k];
        });
      poly(c, pts, "#E6DDC9", INK, w);
      for (let i = 0; i < pts.length; i++) {
        const b = pts[(i + 1) % pts.length],
          tone = ["#F7F1E3", "#D8CDB5", "#EDE4D0", "#C9BDA3"][i % 4];
        poly(c, [[r * 0.1 * Math.cos(i), r * 0.1 * Math.sin(i)], pts[i], b], tone, "rgba(120,100,80,0.4)", 1);
      }
      line(c, [[-r * 0.5, -r * 0.2], [-r * 0.1, r * 0.05], [r * 0.2, -r * 0.3], [r * 0.55, r * 0.1]], "#FF8A3D", r * 0.1);
      line(c, [[-r * 0.1, r * 0.05], [0, r * 0.55]], "#FF8A3D", r * 0.08);
      line(c, [[-r * 0.5, -r * 0.2], [-r * 0.1, r * 0.05], [r * 0.2, -r * 0.3], [r * 0.55, r * 0.1]], "#FFE3A8", r * 0.035);
      eye(c, -r * 0.28, r * 0.28, r * 0.11);
      eye(c, r * 0.28, r * 0.28, r * 0.11);
    },
  },
  // 7 召唤画匣 — open paint box with a swirling portal inside.
  {
    over(c, e, r, t) {
      c.save();
      c.beginPath();
      c.rect(-r * 0.58, -r * 0.3, r * 1.16, r * 0.95);
      c.clip();
      for (let i = 0; i < 3; i++) {
        c.beginPath();
        for (let a = 0; a < 9; a += 0.3) {
          const rr = a * r * 0.055,
            ang = a + t * 0.12 + (i * TAU) / 3;
          c.lineTo(Math.cos(ang) * rr, r * 0.18 + Math.sin(ang) * rr);
        }
        c.strokeStyle = ["#C58BFF", "#FF7BD1", "#7FE9FF"][i];
        c.lineWidth = 2.5;
        c.stroke();
      }
      c.restore();
      glow(c, 0, r * 0.18, r * 0.6, "#B06BFF", 0.8);
    },
    body(c, r) {
      const w = lw(r);
      box(c, -r * 0.9, -r * 1.25, r * 1.8, r * 0.7, r * 0.1, lit(c, r, "#9A6A44"), INK, w);
      for (let i = 0; i < 4; i++) disc(c, -r * 0.6 + i * r * 0.4, -r * 0.9, r * 0.13, ["#FF5C6E", "#FFC94D", "#4DB8FF", "#6DD48A"][i], INK, 1.5);
      box(c, -r * 0.9, -r * 0.45, r * 1.8, r * 1.3, r * 0.12, lit(c, r, "#7B5236"), INK, w);
      box(c, -r * 0.62, -r * 0.32, r * 1.24, r * 1.0, r * 0.08, "#231836", INK, w * 0.7);
      for (const [x, y] of [[-0.9, -0.45], [0.9, -0.45], [-0.9, 0.85], [0.9, 0.85]]) disc(c, x * r, y * r, r * 0.13, "#E4B650", INK, 1.5);
      box(c, -r * 0.14, r * 0.78, r * 0.28, r * 0.16, 2, "#E4B650", INK, 1.5);
    },
  },
  // 8 冲锋剪刀 — steel blades that scissor while charging.
  {
    body(c, r) {
      const w = lw(r);
      for (const s of [-1, 1]) {
        c.beginPath();
        c.ellipse(s * r * 0.48, -r * 0.82, r * 0.34, r * 0.26, s * 0.3, 0, TAU);
        c.ellipse(s * r * 0.48, -r * 0.82, r * 0.18, r * 0.12, s * 0.3, 0, TAU, true);
        c.fillStyle = s < 0 ? DANGER : "#E0304F";
        c.fill("evenodd");
        c.strokeStyle = INK;
        c.lineWidth = w;
        c.stroke();
      }
    },
    over(c, e, r, t) {
      const open = 0.1 + Math.abs(Math.sin(t * (e.age < 110 ? 0.08 : 0.4))) * 0.22,
        w = lw(r);
      for (const s of [-1, 1]) {
        c.save();
        c.translate(0, -r * 0.15);
        c.rotate(s * open);
        poly(c, [[s * r * 0.3, -r * 0.62], [s * r * 0.16, r * 1.35], [-s * r * 0.08, r * 0.35], [-s * r * 0.05, -r * 0.2]], linear(c, -r * 0.3, 0, r * 0.3, 0, s < 0 ? [[0, "#EEF3F7"], [1, "#8C98A8"]] : [[0, "#B7C2CF"], [1, "#F7FAFC"]]), INK, w);
        c.restore();
      }
      disc(c, 0, -r * 0.15, r * 0.14, "#E4B650", INK, 1.8);
    },
  },
  // 9 狙击钢笔 — lacquered fountain pen with a golden nib.
  {
    over(c, e, r, t) {
      if (e.warnAt && e.warnAt > t) glow(c, 0, r * 1.35, r * (0.55 + 0.25 * Math.sin(t * 0.9)), "#6FA8FF", 1);
    },
    body(c, r) {
      const w = lw(r),
        hw = r * 0.3;
      for (const s of [-1, 1]) poly(c, [[s * hw, -r * 0.2], [s * r * 0.75, r * 0.1], [s * r * 0.7, r * 0.3], [s * hw, r * 0.25]], "#2A3F7A", INK, w);
      box(c, -hw, -r * 1.35, hw * 2, r * 1.75, hw, linear(c, -hw, 0, hw, 0, [[0, "#35508F"], [0.35, "#6A87C9"], [1, "#152348"]]), INK, w);
      for (const y of [-r * 0.55, -r * 0.45]) box(c, -hw - 1, y, hw * 2 + 2, r * 0.07, 1, "#E7BC52", INK, 1);
      box(c, hw * 0.1, -r * 1.25, hw * 0.45, r * 0.9, hw * 0.2, "#E7BC52", INK, 1.5);
      poly(c, [[-hw * 0.9, r * 0.4], [hw * 0.9, r * 0.4], [hw * 0.55, r * 0.95], [0, r * 1.38], [-hw * 0.55, r * 0.95]], linear(c, -hw, 0, hw, 0, [[0, "#FFE9A0"], [1, "#C4912C"]]), INK, w);
      line(c, [[0, r * 0.7], [0, r * 1.33]], INK, 1.4);
      disc(c, 0, r * 0.68, r * 0.07, INK);
    },
  },
  // 10 布雷墨瓶 — ink bottle mine-layer with sloshing ink.
  {
    over(c, e, r, t) {
      for (let i = 0; i < 3; i++) {
        const y = r * 0.6 - ((t * 0.6 + i * 14) % (r * 1.1));
        disc(c, -r * 0.3 + i * r * 0.28, y, r * 0.06 + i * 0.6, "rgba(190,210,255,0.7)");
      }
    },
    body(c, r) {
      const w = lw(r);
      for (const s of [-1, 1]) box(c, s * r * 0.95 - r * 0.15, -r * 0.2, r * 0.3, r * 0.7, r * 0.1, "#5D6477", INK, w * 0.8);
      box(c, -r * 0.8, -r * 0.6, r * 1.6, r * 1.55, r * 0.35, linear(c, -r, 0, r, 0, [[0, "#3C4F8F"], [0.5, "#1C2754"], [1, "#0F1633"]]), INK, w);
      box(c, -r * 0.5, -r * 0.1, r * 1.0, r * 0.6, r * 0.06, "#F2E8CF", INK, w * 0.6);
      c.fillStyle = "#1C2754";
      c.beginPath();
      c.moveTo(0, r * 0.02);
      c.quadraticCurveTo(r * 0.18, r * 0.25, 0, r * 0.38);
      c.quadraticCurveTo(-r * 0.18, r * 0.25, 0, r * 0.02);
      c.fill();
      box(c, -r * 0.34, -r * 1.0, r * 0.68, r * 0.45, r * 0.08, "#2A3462", INK, w);
      box(c, -r * 0.42, -r * 1.3, r * 0.84, r * 0.36, r * 0.12, lit(c, r, "#B98A5A"), INK, w);
      line(c, [[-r * 0.62, -r * 0.35], [-r * 0.62, r * 0.6]], "rgba(200,225,255,0.55)", r * 0.08);
    },
  },
  // 11 回旋飞镖 — spinning four-blade star.
  {
    spin: (e) => e.age * 0.24,
    body(c, r) {
      const w = lw(r);
      for (let i = 0; i < 4; i++) {
        c.save();
        c.rotate((i / 4) * TAU);
        poly(c, [[0, -r * 0.25], [r * 0.28, -r * 0.2], [r * 0.12, -r * 1.2]], "#7C8898", INK, w);
        poly(c, [[0, -r * 0.25], [r * 0.12, -r * 1.2], [-r * 0.2, -r * 0.22]], "#E3E9EF", INK, w);
        line(c, [[r * 0.12, -r * 1.2], [r * 0.24, -r * 0.3]], DANGER, 2);
        c.restore();
      }
      disc(c, 0, 0, r * 0.32, "#40495A", INK, w);
      disc(c, 0, 0, r * 0.14, DANGER);
    },
  },
  // 12 激光尺规 — translucent set square with a lens at the tip.
  {
    over(c, e, r, t) {
      const charging = e.warnAt && e.warnAt > t;
      glow(c, 0, r * 0.95, r * (charging ? 0.8 + 0.25 * Math.sin(t) : 0.4), DANGER, 1);
    },
    body(c, r) {
      const w = lw(r),
        outer = [[-r * 1.1, -r * 0.75], [r * 1.1, -r * 0.75], [0, r * 1.05]],
        inner = [[-r * 0.52, -r * 0.46], [r * 0.52, -r * 0.46], [0, r * 0.4]];
      c.beginPath();
      [outer, inner].forEach((pts, k) => {
        const list = k ? [...pts].reverse() : pts;
        list.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
        c.closePath();
      });
      c.fillStyle = "rgba(96,210,196,0.82)";
      c.fill();
      c.strokeStyle = INK;
      c.lineWidth = w;
      c.stroke();
      c.strokeStyle = "rgba(20,60,60,0.6)";
      c.lineWidth = 1.2;
      c.beginPath();
      for (let i = 1; i < 12; i++) {
        const x = -r * 1.1 + (i / 12) * r * 2.2;
        c.moveTo(x, -r * 0.75);
        c.lineTo(x, -r * 0.75 + (i % 3 ? r * 0.1 : r * 0.2));
      }
      c.stroke();
      line(c, [[-r * 0.9, -r * 0.66], [-r * 0.1, r * 0.72]], "rgba(255,255,255,0.6)", 2);
      disc(c, 0, r * 0.82, r * 0.22, "#3A1F2C", INK, w * 0.8);
      disc(c, 0, r * 0.82, r * 0.13, DANGER);
    },
  },
  // 13 弹跳橡皮 — two-tone eraser that squashes as it bounces.
  {
    body(c, r) {
      const w = lw(r);
      box(c, -r * 0.95, -r * 0.62, r * 1.9, r * 1.24, r * 0.3, "#F28DA6", INK, w);
      c.save();
      c.beginPath();
      c.rect(0, -r, r * 1.2, r * 2);
      c.clip();
      box(c, -r * 0.95, -r * 0.62, r * 1.9, r * 1.24, r * 0.3, "#7FA8E6", INK, w);
      c.restore();
      box(c, -r * 0.42, -r * 0.66, r * 0.84, r * 1.32, 2, "#F6F1E4", INK, w * 0.8);
      for (let i = 0; i < 3; i++) line(c, [[-r * 0.28, -r * 0.35 + i * r * 0.18], [r * 0.28, -r * 0.35 + i * r * 0.18]], "rgba(60,70,90,0.45)", 1.6);
      eye(c, -r * 0.66, r * 0.2, r * 0.11);
      eye(c, r * 0.66, r * 0.2, r * 0.11);
      oval(c, -r * 0.7, -r * 0.35, r * 0.16, r * 0.08, "rgba(255,255,255,0.6)");
    },
  },
  // 14 护航纸鸢 — patchwork kite trailing bow-tied tail.
  {
    under(c, e, r, t) {
      const pts: number[][] = [];
      for (let i = 0; i <= 12; i++) pts.push([Math.sin(t * 0.1 + i * 0.6) * r * 0.35, -r * 1.1 - i * r * 0.26]);
      line(c, pts, INK, 2);
      for (let i = 3; i <= 12; i += 3) {
        const [x, y] = pts[i];
        poly(c, [[x, y], [x - r * 0.25, y - r * 0.12], [x - r * 0.25, y + r * 0.12]], i % 6 ? "#FFC94D" : DANGER, INK, 1.3);
        poly(c, [[x, y], [x + r * 0.25, y - r * 0.12], [x + r * 0.25, y + r * 0.12]], i % 6 ? "#FFC94D" : DANGER, INK, 1.3);
      }
    },
    body(c, r) {
      const w = lw(r),
        top = [0, -r * 1.15],
        right = [r * 0.92, -r * 0.1],
        bottom = [0, r * 1.1],
        left = [-r * 0.92, -r * 0.1],
        mid = [0, -r * 0.1];
      poly(c, [top, right, mid], "#E0475B", null);
      poly(c, [right, bottom, mid], "#3FA7D6", null);
      poly(c, [bottom, left, mid], "#FFC94D", null);
      poly(c, [left, top, mid], "#F4EFE3", null);
      poly(c, [top, right, bottom, left], "rgba(0,0,0,0)", INK, w);
      line(c, [top, bottom], "#5A3B22", 2.2);
      line(c, [left, right], "#5A3B22", 2.2);
      eye(c, 0, -r * 0.1, r * 0.14);
    },
  },
  // 15 补给运输机 — four-prop cargo plane with a loot beacon.
  {
    span: 3.6,
    over(c, e, r, t) {
      for (const x of [-1.05, -0.55, 0.55, 1.05]) {
        c.save();
        c.translate(x * r, r * 0.02);
        c.rotate(t * 0.9 + x);
        c.globalAlpha = 0.55;
        oval(c, 0, 0, r * 0.3, r * 0.3, "rgba(230,236,240,0.35)");
        line(c, [[-r * 0.3, 0], [r * 0.3, 0]], "#2B3140", 2.5);
        c.restore();
      }
      glow(c, 0, -r * 0.3, r * (0.45 + 0.15 * Math.sin(t * 0.2)), "#FFD65C", 0.55);
    },
    body(c, r) {
      const w = lw(r);
      poly(c, [[-r * 1.5, -r * 0.2], [r * 1.5, -r * 0.2], [r * 1.42, r * 0.22], [-r * 1.42, r * 0.22]], lit(c, r, "#C7B07A"), INK, w);
      for (const x of [-1.05, -0.55, 0.55, 1.05]) box(c, x * r - r * 0.12, -r * 0.1, r * 0.24, r * 0.5, r * 0.1, "#6E6450", INK, w * 0.8);
      box(c, -r * 0.32, -r * 1.2, r * 0.64, r * 2.2, r * 0.3, lit(c, r, "#D8C38E"), INK, w);
      poly(c, [[-r * 0.6, -r * 1.05], [r * 0.6, -r * 1.05], [r * 0.5, -r * 0.85], [-r * 0.5, -r * 0.85]], "#B39A63", INK, w * 0.8);
      disc(c, 0, -r * 0.3, r * 0.24, "#F6F0DC", INK, 1.8);
      c.fillStyle = "#2FA66E";
      c.fillRect(-r * 0.05, -r * 0.46, r * 0.1, r * 0.32);
      c.fillRect(-r * 0.16, -r * 0.35, r * 0.32, r * 0.1);
      canopy(c, 0, r * 0.72, r * 0.18, r * 0.2);
    },
  },
];

/* ------------------------------------------------------------ ground */

function groundBase(c: Ctx, r: number, kind: number) {
  const w = lw(r);
  if (kind === 16) {
    const oct = Array.from({ length: 8 }, (_, i) => [Math.cos((i / 8) * TAU + Math.PI / 8) * r * 1.02, Math.sin((i / 8) * TAU + Math.PI / 8) * r * 1.02]);
    poly(c, oct, lit(c, r, "#9CA290"), INK, w);
    c.save();
    trace8(c, r * 0.86);
    c.clip();
    for (let i = -4; i <= 4; i++) line(c, [[i * r * 0.3 - r, r * 1.2], [i * r * 0.3 + r, -r * 1.2]], i % 2 ? "rgba(255,200,60,0.55)" : "rgba(30,30,30,0.35)", r * 0.14, "butt");
    c.restore();
    poly(c, oct.map(([x, y]) => [x * 0.78, y * 0.78]), "#7D8574", INK, w * 0.7);
    rivets(c, oct.map(([x, y]) => [x * 0.9, y * 0.9]), r * 0.05);
  } else if (kind === 17) {
    for (const s of [-1, 1]) box(c, s * r * 0.75 - r * 0.25, -r * 1.0, r * 0.5, r * 2.0, r * 0.18, "#353B31", INK, w);
    box(c, -r * 0.62, -r * 0.88, r * 1.24, r * 1.76, r * 0.2, lit(c, r, "#707F4E"), INK, w);
    box(c, -r * 0.45, r * 0.55, r * 0.9, r * 0.22, 3, "#586636", INK, w * 0.6);
    for (const s of [-1, 1]) disc(c, s * r * 0.4, -r * 0.7, r * 0.09, "#3B4428");
  } else if (kind === 18) {
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU;
      oval(c, Math.cos(a) * r * 0.92, Math.sin(a) * r * 0.92, r * 0.28, r * 0.2, i % 2 ? "#C9B07E" : "#B89D6B", INK, 1.5, a + Math.PI / 2);
    }
    box(c, -r * 0.78, -r * 0.78, r * 1.56, r * 1.56, r * 0.12, "#8A8E76", INK, w);
    c.strokeStyle = "rgba(240,230,200,0.6)";
    c.lineWidth = 2;
    c.beginPath();
    c.arc(0, 0, r * 0.6, 0, TAU);
    c.stroke();
  } else {
    const len = r * 2.6;
    c.fillStyle = "#6E5A45";
    for (let x = -len; x <= len; x += r * 0.3) c.fillRect(x - 3, -r * 0.62, 6, r * 1.24);
    for (const y of [-r * 0.42, r * 0.42]) line(c, [[-len - 6, y], [len + 6, y]], "#A3A8AE", 3.5, "butt");
    box(c, -r * 1.3, -r * 0.62, r * 2.6, r * 1.24, r * 0.2, lit(c, r, "#8C7B62"), INK, w);
    for (const x of [-0.85, 0.85]) box(c, x * r - r * 0.35, -r * 0.46, r * 0.7, r * 0.92, 4, "#76674F", INK, w * 0.6);
    rivets(c, [-1.15, -0.55, 0.55, 1.15].flatMap((x) => [[x * r, -r * 0.5], [x * r, r * 0.5]]), r * 0.05);
  }
}
function trace8(c: Ctx, rr: number) {
  c.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * TAU + Math.PI / 8;
    c.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
  }
  c.closePath();
}
function groundHead(c: Ctx, r: number, kind: number) {
  const w = lw(r);
  if (kind === 18) {
    box(c, -r * 0.5, -r * 0.5, r * 1.0, r * 1.05, r * 0.1, lit(c, r, "#5F6E57"), INK, w);
    for (let i = 0; i < 6; i++) {
      const x = -r * 0.3 + (i % 3) * r * 0.3,
        y = r * 0.2 + Math.floor(i / 3) * r * 0.28;
      disc(c, x, y, r * 0.11, "#E9E4D6", INK, 1.2);
      disc(c, x, y, r * 0.05, DANGER);
    }
    return;
  }
  const barrels = kind === 16 ? [-r * 0.16, r * 0.16] : [0];
  for (const x of barrels) box(c, x - r * 0.08, 0, r * 0.16, r * (kind === 19 ? 0.95 : 1.15), r * 0.05, "#3D463A", INK, w * 0.7);
  const tone = kind === 16 ? "#61786A" : kind === 17 ? "#5E6C3E" : "#6B5F4B";
  disc(c, 0, 0, r * (kind === 16 ? 0.5 : 0.44), lit(c, r * 0.5, tone), INK, w);
  disc(c, -r * 0.12, -r * 0.12, r * 0.14, "rgba(255,255,255,0.3)");
  disc(c, 0, r * 0.12, r * 0.08, DANGER);
}

/* ------------------------------------------------------------ render */

interface Memory {
  x: number;
  vx: number;
  hp: number;
  flash: number;
  seen: number;
}
const memory = new Map<number, Memory>();
let memoryTick = -1;
/** Remembers last positions/HP between frames for banking and hit flashes. */
export function trackEnemies(enemies: Enemy[], tick: number) {
  if (tick === memoryTick) return;
  const jumped = tick < memoryTick || tick - memoryTick > 30;
  memoryTick = tick;
  for (const e of enemies) {
    const m = memory.get(e.id);
    if (!m || jumped) {
      memory.set(e.id, { x: e.x, vx: 0, hp: e.hp, flash: 0, seen: tick });
      continue;
    }
    m.vx = m.vx * 0.8 + (e.x - m.x) * 0.2;
    m.x = e.x;
    if (e.hp < m.hp - 0.01) m.flash = tick;
    m.hp = e.hp;
    m.seen = tick;
  }
  if (memory.size > 120) for (const [id, m] of memory) if (m.seen !== tick) memory.delete(id);
}

const key = (kind: number, r: number) => `enemy:${kind}:${Math.round(r)}`;
/** Art is drawn slightly larger than the collision radius for readability. */
const ART = 1.14;
export function paintEnemy(c: Ctx, e: Enemy, tick: number, shadow = 0.28) {
  const r = e.radius,
    m = memory.get(e.id),
    t = e.age;
  c.save();
  c.translate(e.x, e.y);
  c.scale(ART, ART);
  if (e.altitude === "ground" || e.kind >= 16) {
    const kind = clamp(e.kind, 16, 19),
      size = r * 5.8;
    oval(c, 4, 6, r * (kind === 19 ? 2.8 : 1.3), r * 0.85, "rgba(16,20,28,0.3)");
    stamp(c, key(kind, r), size, size, (g) => groundBase(g, r, kind));
    if (kind === 17) {
      c.save();
      c.fillStyle = "rgba(20,20,20,0.5)";
      for (const s of [-1, 1])
        for (let i = 0; i < 6; i++) c.fillRect(s * r * 0.75 - r * 0.2, -r * 0.95 + ((i * r * 0.36 + t * 0.6) % (r * 1.9)), r * 0.4, 2);
      c.restore();
    }
    const aim = (e.aim ?? Math.PI / 2) - Math.PI / 2;
    stamp(c, key(kind + 100, r), r * 3, r * 3, (g) => groundHead(g, r, kind), 0, 0, aim);
    if (m && tick - m.flash < 4) {
      c.globalAlpha = 0.6;
      silhouette(c, key(kind, r), size, size, (g) => groundBase(g, r, kind), "#FFFFFF");
    }
    c.restore();
    return;
  }
  const d = DESIGNS[clamp(e.kind, 0, 15)] ?? DESIGNS[0],
    span = (d.span ?? 3.4) * r,
    bank = clamp(-(m?.vx ?? 0) * 0.05, -0.32, 0.32) + Math.sin(t * 0.05 + e.id) * 0.04,
    spin = d.spin ? d.spin(e, t) : 0,
    elite = !!e.elite;
  // Cast shadow on the terrain below, offset to the lower right.
  c.globalAlpha = shadow;
  if (shadow > 0) silhouette(c, key(e.kind, r), span, span, (g) => d.body(g, r), "#0A1020", r * 0.55, r * 1.1, bank + spin, 0.9);
  c.globalAlpha = 1;
  if (elite) {
    glow(c, 0, 0, r * 1.9, "#FFC94D", 0.55);
    c.save();
    c.rotate(t * 0.03);
    c.strokeStyle = "#FFD65C";
    c.lineWidth = 3;
    c.setLineDash([10, 8]);
    c.beginPath();
    c.arc(0, 0, r + 12, 0, TAU);
    c.stroke();
    c.setLineDash([]);
    c.restore();
  }
  c.rotate(bank);
  if (d.under) d.under(c, e, r, t);
  const squash = e.kind === 13 ? 1 + Math.sin(t * 0.18) * 0.1 : 1;
  c.save();
  if (squash !== 1) c.scale(2 - squash, squash);
  stamp(c, key(e.kind, r), span, span, (g) => d.body(g, r), 0, 0, spin);
  if (d.over) {
    c.rotate(spin);
    d.over(c, e, r, t);
  }
  c.restore();
  if (m && tick - m.flash < 4) {
    c.globalAlpha = 0.65 * (1 - (tick - m.flash) / 4);
    silhouette(c, key(e.kind, r), span, span, (g) => d.body(g, r), "#FFFFFF", 0, 0, spin);
    c.globalAlpha = 1;
  }
  if ((e.slowUntil ?? 0) > tick) {
    c.globalAlpha = 0.4;
    silhouette(c, key(e.kind, r), span, span, (g) => d.body(g, r), "#8FE8FF", 0, 0, spin);
    c.globalAlpha = 1;
  }
  c.restore();
}

/** Health pip above damaged enemies (drawn after all bodies). */
export function paintEnemyHealth(c: Ctx, e: Enemy) {
  if (e.boss || e.hp >= e.maxHp) return;
  const w = Math.max(34, e.radius * 1.7),
    x = e.x - w / 2,
    y = e.y - e.radius - 16,
    k = clamp(e.hp / e.maxHp, 0, 1);
  box(c, x - 1.5, y - 1.5, w + 3, 8, 4, "rgba(16,20,30,0.75)", null);
  box(c, x, y, w * k, 5, 2.5, k > 0.5 ? "#FFD65C" : k > 0.25 ? "#FF9A3D" : DANGER, null);
}

export function burning(c: Ctx, e: Enemy, tick: number) {
  if (!e.burnUntil || e.burnUntil <= tick) return;
  for (let i = 0; i < 3; i++) {
    const a = hash(e.id, i) * TAU,
      life = ((tick + i * 7) % 20) / 20;
    glow(c, e.x + Math.cos(a) * e.radius * 0.5, e.y + Math.sin(a) * e.radius * 0.5 - life * 18, 10 * (1 - life), "#FF7A2E", 0.9);
  }
}
